import React, { FC } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import GaugeCard from './GaugeCard';
import { GaugeProps } from '@components/common/CircleBar/types';

const Gauge: FC<GaugeProps> = ({
  value,
  suffix,
  secondaryValue,
  label,
  progress,
  containerStyle,
  arcColor,
  noData = false,
  noDataText,
  noDataFontSize,
  isPlaceholder = false,
  onPress,
  valueFontSize: valueFontSizeOverride,
  valueColor,
  transparent,
  counterClockwise,
  size,
  scale,
  font,
  C,
  dataSet,
}) => {
  const strokeWidth = scale(6);
  const defaultFontSize = scale(13);
  const valueFontSize = valueFontSizeOverride ?? defaultFontSize;

  // Placeholder state: card without gauge (e.g., "Batch not linked to Job")
  if (isPlaceholder && noDataText) {
    const Container = onPress ? TouchableOpacity : View;
    const containerProps = onPress ? { onPress, activeOpacity: 0.7 } : {}; // will be changed once design is updated

    const placeholderFontSize = noDataFontSize ?? scale(11);
    const placeholderLineHeight = Math.round(placeholderFontSize * 1.45);

    return (
      <Container
        {...containerProps}
        {...(dataSet ? { dataSet } : {})}
        style={{
          flex: 1,
          overflow: 'hidden',
          backgroundColor: C('Gray/Light/7'),
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: C('Hairline/Light/No trans'),
          borderRadius: scale(8),
          padding: scale(4),
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text
          style={[
            font['Time 6'],
            {
              fontSize: placeholderFontSize,
              lineHeight: placeholderLineHeight,
              color: C('Label/Light/Tertiary'),
              textAlign: 'center',
            },
          ]}
        >
          {noDataText}
        </Text>
      </Container>
    );
  }

  // Build center content
  const renderCenterContent = () => {
    // No data state: show "--"
    if (noData) {
      return (
        <Text style={[font['Time 6'], { fontSize: defaultFontSize, lineHeight: scale(20), color: C('Label/Light/Tertiary') }]}>
          --
        </Text>
      );
    }

    // Fraction display (e.g., Pieces: 3/8 or 30 \n /80)
    if (secondaryValue != null) {
      const isSingleLine = value.length <= 1 && secondaryValue.length <= 1;

      if (isSingleLine) {
        return (
          <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
            <Text style={[font['Time 6'], { fontSize: valueFontSize, lineHeight: Math.round(valueFontSize * 1.4), color: C('Label/Light/Primary') }]}>
              {value}
            </Text>
            <Text style={[font['Time 6'], { fontSize: valueFontSize, lineHeight: Math.round(valueFontSize * 1.4), color: C('Label/Light/Tertiary') }]}>
              /{secondaryValue}
            </Text>
          </View>
        );
      }

      return (
        <View style={{ alignItems: 'center', justifyContent: 'center' }}>
          <Text style={[font['Time 6'], { fontSize: valueFontSize, lineHeight: Math.round(valueFontSize * 1.4), color: C('Label/Light/Primary'), textAlign: 'center' }]}>
            {value}
          </Text>
          <Text style={[font['Time 6'], { fontSize: valueFontSize, lineHeight: Math.round(valueFontSize * 1.4), color: C('Label/Light/Tertiary'), textAlign: 'center' }]}>
            /{secondaryValue}
          </Text>
        </View>
      );
    }

    // Value with suffix (e.g., 85%)
    if (suffix) {
      const resolvedColor = valueColor ?? C('Label/Light/Primary');
      return (
        <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
          <Text style={[font['Time 6'], { fontSize: valueFontSize, lineHeight: scale(20), color: resolvedColor }]}>
            {value}
          </Text>
          <Text style={[font['Caption 2'], { fontSize: scale(9), lineHeight: scale(13), color: resolvedColor }]}>
            {suffix}
          </Text>
        </View>
      );
    }

    // Plain value (e.g., -3 days)
    return (
      <Text style={[font['Time 6'], { fontSize: valueFontSize, lineHeight: scale(20), color: valueColor ?? C('Label/Light/Primary') }]}>
        {value}
      </Text>
    );
  };

  return (
    <GaugeCard
      label={label}
      size={size}
      scale={scale}
      font={font}
      C={C}
      containerStyle={containerStyle}
      transparent={transparent}
      dataSet={dataSet}
      circleBarProps={{
        strokeWidth,
        progress,
        color: arcColor,
        trackColor: C('Background/Light/Chart'),
        counterClockwise,
      }}
    >
      {renderCenterContent()}
    </GaugeCard>
  );
};

export default Gauge;
